import React, { useState } from "react";
import { Table } from "react-bootstrap";
import { formatNumber } from "pages/MatchDetail/MatchedOdds";

interface FancyPLData {
  sport_name: string;
  uplineProfitLoss: number;
  total: number;
  commission: number;
  type: number;
  sport_id: string;
}

interface FancyPLTableProps {
  fancyPLData: FancyPLData[];
  onRowClick: (sport_id: string, sport_name: string, type: number) => void;
}

const defaultSportOrder = [
  "Cricket",
  "Session",
  "Soccer",
  "Tennis",
  "Horse Racing",
  "Greyhound Racing",
  "Casino",
];

const sortData = <T extends object>(
  data: T[],
  sortKey: keyof T | "",
  sortOrder: "asc" | "desc"
): T[] => {
  if (!sortKey) return data;

  return [...data].sort((a, b) => {
    const aVal = a[sortKey];
    const bVal = b[sortKey];

    if (typeof aVal === "string" && typeof bVal === "string") {
      return sortOrder === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    const aNum = typeof aVal === "number" ? aVal : parseFloat(aVal as string);
    const bNum = typeof bVal === "number" ? bVal : parseFloat(bVal as string);

    if (isNaN(aNum) || isNaN(bNum)) return 0;

    return sortOrder === "asc" ? aNum - bNum : bNum - aNum;
  });
};

const getSortClass = (
  key: string,
  sortKey: string,
  sortOrder: "asc" | "desc"
) => {
  const isActive = sortKey === key;
  const upClass = isActive && sortOrder === "asc" ? "text-dark" : "text-muted";
  const downClass =
    isActive && sortOrder === "desc" ? "text-dark" : "text-muted";

  return (
    <span className="d-flex flex-column ms-2">
      <i
        className={`bi bi-caret-up-fill ${upClass}`}
        style={{ lineHeight: 0.6, cursor: "pointer" }}
      />
      <i
        className={`bi bi-caret-down-fill ${downClass}`}
        style={{ lineHeight: 0.6, cursor: "pointer" }}
      />
    </span>
  );
};

const FancyPLTableEventPl: React.FC<FancyPLTableProps> = ({
  fancyPLData,
  onRowClick,
}) => {
  const [sortKey, setSortKey] = useState<keyof FancyPLData | "">("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const handleSort = (key: keyof FancyPLData) => {
    if (sortKey === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  const sortedData = sortData(
    [...fancyPLData].sort((a, b) => {
      const aIndex = defaultSportOrder.indexOf(a.sport_name);
      const bIndex = defaultSportOrder.indexOf(b.sport_name);
      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    }),
    sortKey,
    sortOrder
  );

  return (
    <div className="table-responsive">
      <Table striped hover responsive className="table-centered align-middle">
        <thead className="table-light">
          <tr>
            <th
              onClick={() => handleSort("sport_name")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Sport Name</span>
                {getSortClass("sport_name", String(sortKey), sortOrder)}
              </div>
            </th>
            <th
              onClick={() => handleSort("uplineProfitLoss")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Upline Profit/Loss</span>
                {getSortClass("uplineProfitLoss", String(sortKey), sortOrder)}
              </div>
            </th>
            <th
              onClick={() => handleSort("total")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Downline Profit/Loss</span>
                {getSortClass("total", String(sortKey), sortOrder)}
              </div>
            </th>
            <th
              onClick={() => handleSort("commission")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Commission</span>
                {getSortClass("commission", String(sortKey), sortOrder)}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((item, index) => (
            <tr
              key={index}
              onClick={() =>
                onRowClick(item.sport_id, item.sport_name, item.type)
              }
              style={{ cursor: "pointer" }}
            >
              <td>{item.sport_name}</td>
              <td
                className={
                  -1 * (Number(item.total) + Number(item.commission)) < 0
                    ? "text-danger"
                    : "text-success"
                }
              >
                {formatNumber(
                  (-1 * Number(item.total) + Number(item.commission)).toFixed(2)
                )}
              </td>
              <td className={item.total < 0 ? "text-danger" : "text-success"}>
                {formatNumber(((item.total) ?? 0).toFixed(2))}
              </td>
              <td
                className={-1 * item.commission < 0 ? "text-danger" : "text-success"}
              >
                {(-1 * item.commission)?.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default FancyPLTableEventPl;
